// §4.1 lookup-query helpers.
//
// The authoritative tokenizer is SERVER-SIDE (`lookup/tokenizer.ts`): the
// client sends the raw `q` and the API decides what is an attribute filter,
// what is a required measure and what is residual text. This module mirrors ONE
// rule of that grammar — the measure token — for exactly one purpose: building
// the §4.1 EMPTY-STATE alternative query ("Nearest: 3 units of 70mm² under
// 200 m — show anyway?"). That alternative is "the same search without the
// length constraint", and the client has to know which token to drop.
//
// The grammar is copied verbatim from the server tokenizer and unit-tested
// against the same examples, with a comment on both sides. Nothing here decides
// what the user SEES — the alternative results come from a real second call to
// GET /v1/inventory/lookup; this only shapes that call's parameters.

/** Mirror of the server's MEASURE_RE: `^\d+(\.\d+)?(m|kg)$`, anchored. */
const MEASURE_RE = /^(\d+(?:\.\d+)?)(m|kg)$/iu;

export interface SplitLookupQuery {
  /** The query with its measure token removed (empty when nothing is left). */
  readonly withoutMeasure: string;
  /** The required measure as exact decimal text, when the query carried one. */
  readonly requiredMeasure?: string;
  /** The measure token's UoM code. */
  readonly uom?: "m" | "kg";
}

/**
 * Split a free-text lookup query into "everything else" + the FIRST measure
 * token (the server also takes the first and ignores later ones).
 */
export function splitLookupQuery(q: string): SplitLookupQuery {
  const rest: string[] = [];
  let requiredMeasure: string | undefined;
  let uom: "m" | "kg" | undefined;
  for (const token of q.split(/\s+/u)) {
    if (token.length === 0) continue;
    const match = MEASURE_RE.exec(token);
    if (match?.[1] !== undefined && match[2] !== undefined && requiredMeasure === undefined) {
      requiredMeasure = match[1];
      uom = match[2].toLowerCase() as "m" | "kg";
      continue;
    }
    rest.push(token);
  }
  const out: SplitLookupQuery = { withoutMeasure: rest.join(" ") };
  return requiredMeasure !== undefined && uom !== undefined
    ? { ...out, requiredMeasure, uom }
    : out;
}

/**
 * True when a lookup response is empty in ALL THREE §3.8 groups — the trigger
 * for the §4.1 empty state (and only then is the alternative query issued).
 */
/**
 * How many rows a lookup answer actually carries, across all three groups.
 *
 * The §4.1 empty state needs a NUMBER, not a boolean: "no available units — 3
 * reserved units match this search" is a different sentence from "nothing
 * matched", and the difference is the whole of task #194. A storekeeper holding
 * a drum types its serial, the default filter hides reserved stock, and the
 * screen used to answer with silence — the reason the drum was hidden being the
 * very fact they most needed (that it is held, and for what).
 */
export function countLookupMatches(
  response:
    | {
        unit_matches: readonly unknown[];
        offcut_combos: readonly unknown[];
        bulk_lots: readonly unknown[];
      }
    | undefined,
): number {
  if (response === undefined) return 0;
  return response.unit_matches.length + response.offcut_combos.length + response.bulk_lots.length;
}

export function isEmptyLookup(
  response:
    | {
        unit_matches: readonly unknown[];
        offcut_combos: readonly unknown[];
        bulk_lots: readonly unknown[];
      }
    | undefined,
): boolean {
  if (response === undefined) return false;
  return (
    response.unit_matches.length === 0 &&
    response.offcut_combos.length === 0 &&
    response.bulk_lots.length === 0
  );
}
